// ── CustomWrestlerEditor.js ──────────────────────────────────────────
// Modal for creating or editing a custom wrestler.
// Used in Settings → Custom Content → SUPERSTARS tab.
//
// Storage path: renders/{userId}/wrestler/{wrestlerId}_{timestamp}
// Firestore: officialWrestlers collection with isCustom: true + userId
//
// Props:
//   wrestler: existing wrestler to edit (null = create mode)
//   userId: current user UID
//   onClose: () => void
//   onSaved: (savedWrestler) => void — called after successful save

import { getWrestlerName, getWrestlerNameUpper, wrestlerMatchesSearch, sortWrestlersByName } from './wrestlerUtils';
import React, { useState, useRef } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';

const FIGHT_CLASSES = ['Fighter', 'Bruiser', 'Cruiser', 'Specialist', 'Giant'];
const STATUSES = [
  { id: 'active', label: 'ACTIVE', color: '#2ecc71' },
  { id: 'contract', label: 'CONTRACT', color: '#3498db' },
  { id: 'local', label: 'LOCAL', color: '#e67e22' },
  { id: 'legend', label: 'LEGEND', color: '#d4af37' },
  { id: 'inactive', label: 'INACTIVE', color: '#888' },
];

// All match types — keep in sync with matchTypesDatabase or import from it
const MATCH_TYPES_LIST = [
  { id: 'normal', label: 'Normal' },
  { id: 'extreme_rules', label: 'Extreme Rules' },
  { id: 'hardcore', label: 'Hardcore' },
  { id: 'no_dq', label: 'No DQ' },
  { id: 'street_fight', label: 'Street Fight' },
  { id: 'last_man_standing', label: 'Last Man Standing' },
  { id: 'cage', label: 'Steel Cage' },
  { id: 'hell_in_a_cell', label: 'Hell in a Cell' },
  { id: 'ladder', label: 'Ladder' },
  { id: 'tlc', label: 'TLC' },
  { id: 'tables', label: 'Tables' },
  { id: 'iron_man', label: 'Iron Man' },
  { id: 'submission', label: 'Submission' },
  { id: 'i_quit', label: 'I Quit' },
  { id: 'first_blood', label: 'First Blood' },
  { id: 'falls_count_anywhere', label: 'Falls Count Anywhere' },
  { id: 'casket', label: 'Casket' },
  { id: 'ambulance', label: 'Ambulance' },
  { id: 'buried_alive', label: 'Buried Alive' },
];

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

async function uploadRenderToStorage(file, userId, wrestlerId) {
  const path = 'renders/' + userId + '/wrestler/' + wrestlerId + '_' + Date.now();
  const sref = storageRef(storage, path);
  const snapshot = await uploadBytes(sref, file);
  const url = await getDownloadURL(snapshot.ref);
  return { url, path };
}

async function deleteRenderFromStorage(path) {
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (e) {
    console.warn('Could not delete old render:', e);
  }
}

// ── Slider component (custom) ────────────────────────────────────────
const Slider = ({ value, onChange, min, max, step = 1, color = '#d4af37' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '220px' }}>
    <input
      type="range"
      min={min} max={max} step={step}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      style={{
        flex: 1,
        height: '6px',
        accentColor: color,
        cursor: 'pointer',
      }}
    />
    <div style={{
      ...S.anton,
      fontSize: '14px',
      color: color,
      minWidth: '36px',
      textAlign: 'right',
      letterSpacing: '1px',
    }}>{value}</div>
  </div>
);

// ── Field row ────────────────────────────────────────────────────────
const Field = ({ label, required, children, fullWidth }) => (
  <div style={{
    display: 'flex',
    flexDirection: fullWidth ? 'column' : 'row',
    alignItems: fullWidth ? 'stretch' : 'center',
    justifyContent: 'space-between',
    gap: fullWidth ? '8px' : '12px',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  }}>
    <div style={{
      ...S.mono,
      fontSize: '11px',
      letterSpacing: '1px',
      color: 'rgba(255,255,255,0.7)',
      flexShrink: 0,
    }}>
      {label}
      {required && <span style={{ color: '#e74c3c', marginLeft: '4px' }}>*</span>}
    </div>
    <div style={{ flex: fullWidth ? 'unset' : '0 0 auto' }}>{children}</div>
  </div>
);

// ── Main editor modal ────────────────────────────────────────────────
export default function CustomWrestlerEditor({ wrestler, userId, onClose, onSaved, zIndex = 300 }) {
  const isEdit = !!wrestler;
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(() => ({
    nick_name: wrestler?.nick_name || '',
    first_name: wrestler?.first_name || '',
    middle_name: wrestler?.middle_name || '',
    last_name: wrestler?.last_name || '',
    gender: wrestler?.gender || 'M',
    fight_class: wrestler?.fight_class || 'Fighter',
    heel: wrestler?.heel ?? false,
    starting_xp: wrestler?.starting_xp ?? 10,
    promo_skill: wrestler?.promo_skill ?? 1,
    popularity: wrestler?.popularity ?? 0,
    status: wrestler?.status || 'active',
    match_types: wrestler?.match_types || [],
    customRender: wrestler?.customRender || wrestler?.render || null,
    customRenderPath: wrestler?.customRenderPath || null,
  }));

  const getInitialRender = () => {
    if (wrestler?.customRender) return wrestler.customRender;
    if (wrestler?.render) return wrestler.render;
    // Official wrestler render from /wrestlers/ folder
    if (wrestler?.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + wrestler.wrestler_id + '-render.png';
    return null;
  };

  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(getInitialRender);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large (max 5MB)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }
    setError(null);
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const toggleMatchType = (id) => {
    setForm(f => {
      const has = f.match_types.includes(id);
      return { ...f, match_types: has ? f.match_types.filter(x => x !== id) : [...f.match_types, id] };
    });
  };

  const handleSave = async () => {
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (!previewUrl && !pendingFile && !form.render) {
      setError('Render image is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Generate or reuse wrestler_id
      const wrestlerId = isEdit
        ? String(wrestler.wrestler_id)
        : 'custom_' + userId.slice(0, 6) + '_' + Date.now();

      // Upload new image if pending
      let renderUrl = form.customRender;
      let renderPath = form.customRenderPath;
      if (pendingFile) {
        // Delete old image if editing
        if (isEdit && form.customRenderPath) {
          await deleteRenderFromStorage(form.customRenderPath);
        }
        const uploaded = await uploadRenderToStorage(pendingFile, userId, wrestlerId);
        renderUrl = uploaded.url;
        renderPath = uploaded.path;
      }

      // Build the wrestler doc
      const computedName = [form.nick_name, form.first_name, form.middle_name, form.last_name].filter(Boolean).join(' ') || form.first_name;
      const wrestlerData = {
        wrestler_id: wrestlerId, // toujours string
        name: computedName,
        nick_name: form.nick_name.trim(),
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim(),
        last_name: form.last_name.trim(),
        gender: form.gender,
        fight_class: form.fight_class,
        heel: form.heel,
        starting_xp: Number(form.starting_xp),
        promo_skill: Number(form.promo_skill),
        popularity: Number(form.popularity),
        status: form.status,
        match_types: form.match_types,
        customRender: renderUrl,
        render: renderUrl,
        customRenderPath: renderPath,
        isCustom: true,
        userId,
        updatedAt: new Date().toISOString(),
      };

      if (!isEdit) {
        wrestlerData.createdAt = new Date().toISOString();
      }

      // Save to Firestore
      if (isEdit && wrestler._docId) {
        await updateDoc(doc(db, 'officialWrestlers', wrestler._docId), wrestlerData);
      } else if (isEdit) {
        // No _docId — find by wrestler_id
        const q = query(collection(db, 'officialWrestlers'), where('wrestler_id', '==', wrestlerId), where('userId', '==', userId));
        const snap = await getDocs(q);
        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, wrestlerData);
        } else {
          // Fallback: create new doc with deterministic ID = wrestler_id
          await setDoc(doc(db, 'officialWrestlers', wrestlerId), wrestlerData);
        }
      } else {
        // Create — use deterministic doc ID = wrestler_id for easier lookups
        await setDoc(doc(db, 'officialWrestlers', wrestlerId), wrestlerData);
      }

      // Clear sessionStorage cache so the new wrestler shows up immediately
      try {
        sessionStorage.removeItem('officialWrestlers');
      } catch {}

      // Dispatch global event to notify RecruitmentTab and other listeners
      try {
        window.dispatchEvent(new CustomEvent('wrestlersUpdated', { detail: wrestlerData }));
      } catch {}

      onSaved && onSaved(wrestlerData);
    } catch (e) {
      console.error('Save failed:', e);
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={() => !saving && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: zIndex,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'rgba(8,8,8,0.98)',
          border: '1px solid rgba(212,175,55,0.3)',
          borderRadius: '10px',
          width: '100%',
          maxWidth: '720px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 24px rgba(212,175,55,0.15)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ ...S.anton, fontSize: '20px', letterSpacing: '4px', color: '#fff' }}>
              {isEdit ? 'EDIT SUPERSTAR' : 'NEW CUSTOM SUPERSTAR'}
            </div>
            <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
              {isEdit ? 'Update the wrestler details below' : 'Add a new modded wrestler to your roster'}
            </div>
          </div>
          <button onClick={onClose} disabled={saving}
            style={{
              width: '32px', height: '32px',
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.7)',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 28px', display: 'grid', gridTemplateColumns: '200px 1fr', gap: '24px' }}>
          {/* Left: render preview + upload */}
          <div>
            <div style={{
              width: '180px',
              height: '240px',
              background: 'radial-gradient(ellipse at bottom, rgba(212,175,55,0.1) 0%, transparent 70%), rgba(0,0,0,0.4)',
              border: '1px dashed rgba(255,255,255,0.15)',
              borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
              marginBottom: '12px',
            }}>
              {previewUrl ? (
                <img loading="lazy" src={previewUrl} alt="Render preview"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                  NO RENDER<br />UPLOADED
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button onClick={() => fileInputRef.current?.click()} disabled={saving}
              style={{
                ...S.mono, fontSize: '10px', letterSpacing: '1.5px',
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.4)',
                color: '#d4af37',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '180px',
              }}>
              {previewUrl ? 'CHANGE RENDER' : 'UPLOAD RENDER'}
            </button>
            <div style={{ ...S.mono, fontSize: '9px', color: 'rgba(255,255,255,0.3)', marginTop: '6px', textAlign: 'center', letterSpacing: '0.5px', lineHeight: 1.6 }}>
              PNG / JPG · max 5 MB<br />
              Recommended: 1024×1024 (1:1)
            </div>
          </div>

          {/* Right: form fields */}
          <div>
            <Field label="Nick name">
              <input type="text" value={form.nick_name} onChange={e => update('nick_name', e.target.value)} disabled={saving}
                placeholder="e.g. Stone Cold"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', ...S.mono, fontSize: '12px', padding: '7px 10px', borderRadius: '4px', minWidth: '240px', outline: 'none' }} />
            </Field>

            <Field label="First name" required>
              <input type="text" value={form.first_name} onChange={e => update('first_name', e.target.value)} disabled={saving}
                placeholder="e.g. Steve"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', ...S.mono, fontSize: '12px', padding: '7px 10px', borderRadius: '4px', minWidth: '240px', outline: 'none' }} />
            </Field>

            <Field label="Middle name">
              <input type="text" value={form.middle_name} onChange={e => update('middle_name', e.target.value)} disabled={saving}
                placeholder="e.g. The Hitman"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', ...S.mono, fontSize: '12px', padding: '7px 10px', borderRadius: '4px', minWidth: '240px', outline: 'none' }} />
            </Field>

            <Field label="Last name">
              <input type="text" value={form.last_name} onChange={e => update('last_name', e.target.value)} disabled={saving}
                placeholder="e.g. Austin"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', ...S.mono, fontSize: '12px', padding: '7px 10px', borderRadius: '4px', minWidth: '240px', outline: 'none' }} />
            </Field>

            <Field label="Gender" required>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['M', 'F'].map(g => (
                  <button key={g} onClick={() => update('gender', g)} disabled={saving}
                    style={{
                      ...S.anton, fontSize: '12px', letterSpacing: '2px',
                      background: form.gender === g ? 'rgba(212,175,55,0.2)' : 'transparent',
                      border: '1px solid ' + (form.gender === g ? '#d4af37' : 'rgba(255,255,255,0.15)'),
                      color: form.gender === g ? '#d4af37' : 'rgba(255,255,255,0.6)',
                      padding: '6px 16px', borderRadius: '4px',
                      cursor: 'pointer',
                    }}>{g === 'M' ? 'MALE' : 'FEMALE'}</button>
                ))}
              </div>
            </Field>

            <Field label="Fight class" required>
              <select value={form.fight_class} onChange={e => update('fight_class', e.target.value)} disabled={saving}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#fff',
                  ...S.mono, fontSize: '12px',
                  padding: '7px 10px', borderRadius: '4px',
                  outline: 'none',
                  colorScheme: 'dark',
                  minWidth: '160px',
                }}>
                {FIGHT_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>

            <Field label="Role" required>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => update('heel', false)} disabled={saving}
                  style={{
                    ...S.anton, fontSize: '12px', letterSpacing: '2px',
                    background: !form.heel ? 'rgba(46,204,113,0.2)' : 'transparent',
                    border: '1px solid ' + (!form.heel ? '#2ecc71' : 'rgba(255,255,255,0.15)'),
                    color: !form.heel ? '#2ecc71' : 'rgba(255,255,255,0.6)',
                    padding: '6px 16px', borderRadius: '4px',
                    cursor: 'pointer',
                  }}>FACE</button>
                <button onClick={() => update('heel', true)} disabled={saving}
                  style={{
                    ...S.anton, fontSize: '12px', letterSpacing: '2px',
                    background: form.heel ? 'rgba(231,76,60,0.2)' : 'transparent',
                    border: '1px solid ' + (form.heel ? '#e74c3c' : 'rgba(255,255,255,0.15)'),
                    color: form.heel ? '#e74c3c' : 'rgba(255,255,255,0.6)',
                    padding: '6px 16px', borderRadius: '4px',
                    cursor: 'pointer',
                  }}>HEEL</button>
              </div>
            </Field>

            <Field label="Starting XP" required>
              <Slider value={form.starting_xp} onChange={v => update('starting_xp', v)} min={1} max={30} color="#d4af37" />
            </Field>

            <Field label="Promo skill" required>
              <Slider value={form.promo_skill} onChange={v => update('promo_skill', v)} min={1} max={5} color="#9b59b6" />
            </Field>

            <Field label="Popularity">
              <Slider value={form.popularity} onChange={v => update('popularity', v)} min={0} max={100} color="#3498db" />
            </Field>

            <Field label="Status" required>
              <select value={form.status} onChange={e => update('status', e.target.value)} disabled={saving}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#fff',
                  ...S.mono, fontSize: '12px',
                  padding: '7px 10px', borderRadius: '4px',
                  outline: 'none',
                  colorScheme: 'dark',
                  minWidth: '160px',
                }}>
                {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>

            <Field label="Match types" fullWidth>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                {MATCH_TYPES_LIST.map(m => {
                  const sel = form.match_types.includes(m.id);
                  return (
                    <button key={m.id} onClick={() => toggleMatchType(m.id)} disabled={saving}
                      style={{
                        ...S.mono, fontSize: '9px', letterSpacing: '0.5px',
                        background: sel ? 'rgba(212,175,55,0.15)' : 'transparent',
                        border: '1px solid ' + (sel ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.12)'),
                        color: sel ? '#d4af37' : 'rgba(255,255,255,0.5)',
                        padding: '4px 10px', borderRadius: '3px',
                        cursor: 'pointer',
                      }}>{m.label}</button>
                  );
                })}
              </div>
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 28px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '0.5px', color: error ? '#e74c3c' : 'rgba(255,255,255,0.3)' }}>
            {error || (isEdit ? 'ID: ' + (wrestler.wrestler_id || '—') : 'A unique ID will be auto-generated')}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onClose} disabled={saving}
              style={{
                ...S.mono, fontSize: '10px', letterSpacing: '1.5px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.7)',
                padding: '7px 16px', borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}>CANCEL</button>
            <button onClick={handleSave} disabled={saving || !form.first_name.trim()}
              style={{
                ...S.mono, fontSize: '10px', letterSpacing: '1.5px',
                background: 'rgba(46,204,113,0.15)',
                border: '1px solid rgba(46,204,113,0.5)',
                color: '#2ecc71',
                padding: '7px 20px', borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving || !form.first_name.trim() ? 0.5 : 1,
              }}>{saving ? 'SAVING…' : (isEdit ? 'UPDATE' : 'CREATE')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
